import { useState, useEffect } from "react";

import type { WhereArgument } from "types";
import type { ApolloQueryResult } from "@apollo/client";
import type { SetURLSearchParams } from "react-router-dom";

type PaginationArguments = {
  pageNumber: number,
  pageSize: number,
  where: WhereArgument["where"] | null | undefined
};

type UsePaginationHandlersArgs = {
  pageInfoEndCursor: string | null | undefined,
  listOfCursors: string[] | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchMore: (variables: {variables: any}) => Promise<void> | Promise<ApolloQueryResult<any>>;
  refetchKeyNames?: [string, string, string],
  initialWhereArgument?: PaginationArguments["where"] | undefined,
  initialShowCount?: number,
  setSearchParams?: SetURLSearchParams
};

export default function usePaginationHandlers({
  pageInfoEndCursor, 
  listOfCursors,
  fetchMore,
  refetchKeyNames = ["n", "after", "where"],
  initialWhereArgument,
  initialShowCount,
  setSearchParams
}: UsePaginationHandlersArgs) {

  const [paginationState, setPaginationState] = useState<PaginationArguments>({
    pageNumber: 1,
    pageSize: initialShowCount ?? 10,
    where: initialWhereArgument
  });

  const [lastCursorOfPageState, setLastCursorOfPageState] = useState<string | null>(null);

  const handleNextPage = async (currentPageSize: number, newPageNumber: number, where: PaginationArguments["where"]) => {
    if (lastCursorOfPageState === pageInfoEndCursor) {
      await fetchMore({
        variables: {
          // If the number of items in the list is less than the current page size, fetch the difference
          // Can occur when a charger is uninstalled
          [refetchKeyNames?.[0]]: (listOfCursors?.length ?? 0) < currentPageSize 
            ? currentPageSize + (currentPageSize - (listOfCursors?.length ?? 0))
            : currentPageSize,
          [refetchKeyNames[1]]: pageInfoEndCursor,
          [refetchKeyNames[2]]: where
        }
      });
      setPaginationState({...paginationState, pageNumber: newPageNumber, where: where});
    } else {
      setLastCursorOfPageState(listOfCursors?.[(newPageNumber * currentPageSize) - 1] ?? null);
      setPaginationState({...paginationState, pageNumber: newPageNumber, where: where});
    }
  };

  const handlePrevPage = async (currentPageSize: number, newPageNumber: number, where: PaginationArguments["where"]) => {
    setLastCursorOfPageState(listOfCursors?.[(newPageNumber * currentPageSize) - 1] ?? null);
    setPaginationState({...paginationState, pageNumber: newPageNumber, where: where});
  };

  const handlePageSize = async (newPageSize: number, currentPageNumber: number, where: PaginationArguments["where"]) => {
    if ((newPageSize > (listOfCursors?.length ?? 0))) {
      await fetchMore({
        variables: {
          [refetchKeyNames[0]]: currentPageNumber === 1 ? newPageSize : newPageSize - (listOfCursors?.length ?? 0),
          [refetchKeyNames[1]]: currentPageNumber === 1 ? null : lastCursorOfPageState,
          [refetchKeyNames[2]]: where
        }
      });
    } 
    setPaginationState({pageNumber: 1, pageSize: newPageSize, where: where});
    if (setSearchParams != null) {
      setSearchParams((prevParams) => {
        prevParams.set("show", newPageSize.toString());
        return prevParams;
      });
    }
  };

  useEffect(() => {
    if ((listOfCursors?.length ?? 0) > 0 && listOfCursors?.every((c) => c != null) && lastCursorOfPageState !== listOfCursors?.[(paginationState?.pageNumber * paginationState?.pageSize) - 1]) {
      if (listOfCursors?.[(paginationState?.pageNumber * paginationState?.pageSize) - 1] != null && pageInfoEndCursor !== listOfCursors?.[(paginationState?.pageNumber * paginationState?.pageSize) - 1]) {
        setLastCursorOfPageState(listOfCursors?.[(paginationState?.pageNumber * paginationState?.pageSize) - 1] ?? null);
      } else if (lastCursorOfPageState !== pageInfoEndCursor) {
        setLastCursorOfPageState(pageInfoEndCursor ?? null);
      }
    }
  }, [lastCursorOfPageState, listOfCursors, pageInfoEndCursor, paginationState.pageNumber, paginationState.pageSize, setLastCursorOfPageState]);

  return {
    paginationState,
    setLastCursorOfPageState,
    setPaginationState,
    handleNextPage,
    handlePrevPage,
    handlePageSize,
  };
}